<template>
  <SrpModal :isVisible="isVisible" :isClosable="false" className="auto-post-tagging-announcement-modal" :width="540" size="small" @update:isVisible="onVisibleChange">
    <template #header>
      <div class="auto-post-tagging-announcement-modal__title-row">
        <div class="auto-post-tagging-announcement-modal__icon-circle" aria-hidden="true">
          <IconEmbedded name="map-pin_2" :size="22" color="#118689" />
          <IconEmbedded class="auto-post-tagging-announcement-modal__sparkle" name="ai-solid" :size="12" color="#118689" />
        </div>
        <h2 class="global-h2 auto-post-tagging-announcement-modal__title">Automatic Tagging for New Posts</h2>
      </div>
    </template>
    <template #content>
      <p class="global-p auto-post-tagging-announcement-modal__intro">Newly generated posts now automatically tag locations in your destination.</p>
      <ul class="auto-post-tagging-announcement-modal__list">
        <li>We've created an initial set of locations from your Creator Visits and added social handles where we could.</li>
        <li>
          Review them and add more on the
          <LinkWithIcon tag="router-link" isDottedUnderline color="teal" :to="configLocationsTo" @click="persistDismissal">Config page</LinkWithIcon>.
        </li>
        <li>Posts with locations will include caption mentions and Instagram photo tags by default.</li>
        <li>
          Prefer not to use location tagging?
          <LinkWithIcon tag="router-link" isDottedUnderline color="teal" :to="configAtlasSettingsTo" @click="persistDismissal">Turn it off here</LinkWithIcon>, or adjust tags on any post.
        </li>
      </ul>
    </template>
    <template #footer="{ closeModal }">
      <div class="auto-post-tagging-announcement-modal__footer">
        <p class="auto-post-tagging-announcement-modal__support">
          We hope this helps highlight the places that make your destination special!<br />
          Questions?
          <CopyTextNew>support@shrpa.com</CopyTextNew>
        </p>
        <SrpButton class="auto-post-tagging-announcement-modal__got-it" tag="button" shape="rectangle" @click="acknowledge(closeModal)">Got it</SrpButton>
      </div>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import CopyTextNew from "@components/CopyTextNew.vue";
import { ATLAS_SETTINGS_HASH, LOCATIONS_SECTION_HASH } from "@logic/ContentCalendarHelper";

const STORAGE_KEY = "shrpa-auto-post-tagging-modal-dismissed";
const AUTO_SHOW_UNTIL = new Date(2026, 9, 1);

const props = defineProps<{
  customerId: string;
}>();

function shouldAutoShow(): boolean {
  if (Date.now() >= AUTO_SHOW_UNTIL.getTime()) return false;
  return localStorage.getItem(STORAGE_KEY) !== "true";
}

const isVisible = ref(shouldAutoShow());

const configLocationsTo = computed(() => ({
  name: "CalendarConfig",
  params: { customerId: props.customerId },
  hash: `#${LOCATIONS_SECTION_HASH}`,
}));

const configAtlasSettingsTo = computed(() => ({
  name: "CalendarConfig",
  params: { customerId: props.customerId },
  hash: `#${ATLAS_SETTINGS_HASH}`,
}));

function persistDismissal() {
  localStorage.setItem(STORAGE_KEY, "true");
}

function onVisibleChange(value: boolean) {
  if (!value) persistDismissal();
  isVisible.value = value;
}

function acknowledge(closeModal: () => void) {
  persistDismissal();
  closeModal();
}

function open() {
  isVisible.value = true;
}

defineExpose({ open });
</script>

<style scoped lang="scss">
.auto-post-tagging-announcement-modal {
  &__title-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__icon-circle {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: rgba(17, 134, 137, 0.12);
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    flex-shrink: 0;
  }

  &__sparkle {
    position: absolute;
    top: 6px;
    right: 5px;
  }

  &__title {
    margin: 0;
  }

  &__intro {
    margin: 0;
  }

  &__list {
    list-style: none;
    margin: 16px 0 0;
    padding: 0;
  }

  &__list li {
    position: relative;
    margin-bottom: 12px;
    padding-left: 18px;
    color: #5b5b5b;
    font: 14px/20px sans-serif;
  }

  &__list li:last-child {
    margin-bottom: 0;
  }

  &__list li::before {
    content: "";
    width: 10px;
    height: 2.5px;
    border-radius: 2px;
    background: #118689;
    position: absolute;
    left: 0;
    top: 0.55em;
  }

  &__footer {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  &__support {
    margin: 0;
    color: #5b5b5b;
    font: 14px/20px sans-serif;
  }

  &__got-it {
    align-self: flex-end;
  }
}
</style>

<style lang="scss">
.srp-modal.auto-post-tagging-announcement-modal {
  .srp-modal__footer {
    background: #fff;
    border-top: 1px solid rgba(0, 0, 0, 0.12);
    justify-content: flex-end;
    align-items: stretch;
  }
}
</style>
